import type { PickedAccommodationType } from "@@/shared/acquire/resources/resource-accommodation/accommodation-types/picked-accommodation-type";
import { pickedPickedAccommodationTypeZodObject } from "@@/shared/acquire/resources/resource-accommodation/accommodation-types/picked-accommodation-type";
import { cartIdZodSchema, type CartId } from "@@/shared/carts/cart-id";
import {
    timeRangeZodSchema,
    type TimeRange,
} from "@@/shared/date-time/time-range";
import { unitZodSchema, type Unit } from "@@/shared/units-and-quantities/unit";
import type { Except } from "type-fest";
import { z } from "zod";
import type { BookableOptionValue } from "../resource-reference-options/bookable-option-value";
import { bookableOptionValueZodSchema } from "../resource-reference-options/bookable-option-value-zod-schemas";
import {
    bookableSessionIdZodSchema,
    type BookableSessionId,
} from "../sessions/bookable-session-id";
import { resourceIdZodSchema, type ResourceId } from "./resource-id";
import {
    resourceReservationRequestIdFactory,
    resourceReservationRequestIdZodSchema,
    type ResourceReservationRequestId,
} from "./resource-reservation-request-id";
import type { NonGroupResourceType } from "./resource-type";
import { nonGroupResourceTypeZodEnum } from "./resource-type";

type _ResourceReservationRequest_Common = {
    readonly _id: ResourceReservationRequestId;
    readonly _type: "RESOURCE_RESERVATION_REQUEST";
    readonly timeRange: TimeRange;
    readonly resourceId: ResourceId;
    readonly optionValues: BookableOptionValue[];
    readonly resourceUnit: Unit;
    readonly quantity: number;
    readonly main: boolean;
    readonly sessionId?: BookableSessionId;
    readonly pickedBy: "SYSTEM" | "USER";
    readonly cartId?: CartId;
    readonly displayResourceTitle?: boolean;
    //is last reservation in schedule, should get postTime
    readonly isLastReservation?: boolean;
    //Used by virtual tables
    readonly parentReservationId?: ResourceReservationRequestId;
};

type _ResourceReservationRequest_Generic =
    _ResourceReservationRequest_Common & {
        // Properties available when resource type is not RESOURCE_ACCOMMODATION
        readonly resourceType: Exclude<
            NonGroupResourceType,
            "RESOURCE_ACCOMMODATION"
        >;
    };

type _ResourceReservationRequest_Accommodation =
    _ResourceReservationRequest_Common & {
        // Properties only available when resource type is RESOURCE_ACCOMMODATION
        readonly resourceType: Extract<
            NonGroupResourceType,
            "RESOURCE_ACCOMMODATION"
        >;
        readonly accommodationType?: PickedAccommodationType;
    };

type ResourceReservationRequest =
    | _ResourceReservationRequest_Generic
    | _ResourceReservationRequest_Accommodation;

type resourceReservationRequestFactoryParams = Except<
    ResourceReservationRequest,
    "_type" | "_id"
>;
const resourceReservationRequestFactory = (
    params: resourceReservationRequestFactoryParams,
): ResourceReservationRequest => {
    return {
        ...params,
        _id: resourceReservationRequestIdFactory(),
        _type: "RESOURCE_RESERVATION_REQUEST",
    };
};
const resourceReservationRequestZodObject = z.object({
    _id: resourceReservationRequestIdZodSchema,
    _type: z.literal("RESOURCE_RESERVATION_REQUEST"),
    timeRange: timeRangeZodSchema,
    resourceId: resourceIdZodSchema,
    optionValues: z.array(bookableOptionValueZodSchema),
    resourceType: nonGroupResourceTypeZodEnum,
    resourceUnit: unitZodSchema,
    quantity: z.number().int().min(0),
    main: z.boolean(),
    sessionId: bookableSessionIdZodSchema.optional(),
    pickedBy: z.union([z.literal("SYSTEM"), z.literal("USER")]),
    cartId: cartIdZodSchema.optional(),
    displayResourceTitle: z.boolean().optional(),
    accommodationType: pickedPickedAccommodationTypeZodObject.optional(),
});
const resourceReservationRequestZodSchema: z.ZodSchema<ResourceReservationRequest> =
    resourceReservationRequestZodObject;

const isAccommodationResourceReservationRequest = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    item: any,
): item is _ResourceReservationRequest_Accommodation =>
    item?.resourceType === "RESOURCE_ACCOMMODATION";

export {
    isAccommodationResourceReservationRequest,
    resourceReservationRequestFactory,
    resourceReservationRequestZodObject,
    resourceReservationRequestZodSchema,
};
export type { ResourceReservationRequest };
